/**
 * TanStack Query hooks for Qualitas AML compliance.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { amlComplianceService } from '../services/aml-compliance.service'
import { useAuth, authKeys } from './use-auth'
import { verificationKeys } from './use-verification'
import { ServiceError } from '../errors/service-error'
import type { AmlFormValues } from '../types/aml.types'
import { isAmlFormComplete, isAmlHostReady } from '../types/aml.types'
import { EARLY_ACCESS } from '../utils/constants'

const amlKeys = {
  all: ['aml'] as const,
  declaration: (profileId: string) => [...amlKeys.all, 'declaration', profileId] as const,
  formBootstrap: (profileId: string) => [...amlKeys.all, 'form', profileId] as const,
}

/**
 * The submit path is a chain of independent Supabase round trips (consent →
 * declaration upsert → disclaimer artifacts). Each one is individually bounded
 * by the 15s fetch adapter, but the chain has no ceiling of its own: on a
 * degraded network the "Enviar y continuar" button could sit in `loading` for
 * over a minute while the spinner masquerades as progress. This budget makes
 * the whole mutation settle deterministically — the button always unlocks, and
 * the user always gets one of exactly three outcomes: saved, failed, or
 * "el servidor tardó" with the rows it DID write safe to retry (the upsert and
 * the disclaimer guards are idempotent).
 */
const AML_SUBMIT_BUDGET_MS = 30_000

function withBudget<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new ServiceError({
          message: `AML declaration submit did not settle within ${ms}ms`,
          code: 'AML_SUBMIT_TIMED_OUT',
          statusCode: 504,
          userMessageKey: 'errors.timeout',
        }),
      )
    }, ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function useAmlDeclaration() {
  const { profile } = useAuth()
  const profileId = profile?.id ?? ''

  return useQuery({
    queryKey: amlKeys.declaration(profileId),
    queryFn: () => amlComplianceService.getDeclaration(profileId),
    enabled: !!profileId,
    staleTime: 30_000,
  })
}

export function useAmlFormBootstrap() {
  const { profile } = useAuth()
  const profileId = profile?.id ?? ''

  return useQuery({
    queryKey: amlKeys.formBootstrap(profileId),
    queryFn: () => {
      if (!profile) throw new Error('Profile required')
      return amlComplianceService.buildFormValues(profile)
    },
    enabled: !!profile,
    staleTime: 15_000,
  })
}

export function useSubmitAmlDeclaration() {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: (form: AmlFormValues) => {
      if (!profile?.id) throw new Error('Not authenticated')
      return withBudget(
        amlComplianceService.submitDeclaration(profile.id, form),
        AML_SUBMIT_BUDGET_MS,
      )
    },
    // Never blind-retry a declaration write: the chain is not idempotent at
    // the consent layer (append-only audit rows), and TanStack would otherwise
    // re-fire the whole chain on a transient failure.
    retry: false,
    onSuccess: (declaration) => {
      if (!profile?.id) return
      queryClient.setQueryData(amlKeys.declaration(profile.id), declaration)
      void queryClient.invalidateQueries({ queryKey: amlKeys.formBootstrap(profile.id) })
      void queryClient.invalidateQueries({ queryKey: verificationKeys.overview(profile.id) })
      void queryClient.invalidateQueries({ queryKey: authKeys.profile(profile.id) })
      // La declaración es un bloqueador de `booking_eligibility`. Sin esto, la
      // ficha del vehículo —con su `staleTime` de 10s— seguía diciendo "te
      // falta..." justo después de responder el cuestionario, que es la misma
      // mentira en la dirección contraria.
      void queryClient.invalidateQueries({ queryKey: ['booking-eligibility'] })
    },
  })
}

/** Derived readiness flags for gates (booking / hosting). */
export function useAmlReadiness() {
  const query = useAmlDeclaration()
  const status = query.data?.status ?? null
  const requireHostApproval = EARLY_ACCESS.amlHostRequiresPartnerApproval

  return {
    ...query,
    status,
    isComplete: isAmlFormComplete(status),
    // The OFAC window is passed here, so hosting is the ONLY gate that waits.
    // Booking readiness deliberately does not: a guest paying for a trip is not
    // the registration Quálitas asked to review before it happens.
    isHostReady: isAmlHostReady(
      status,
      requireHostApproval,
      query.data?.exportedToPartnerAt ?? null,
    ),
    isPendingReview: status === 'under_review' || status === 'submitted',
    isRejected: status === 'rejected' || status === 'needs_update',
    isApproved: status === 'approved',
  }
}
